import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchEmployees, editEmployee, deleteEmployee, fetchAllWorkOrders, fetchCustomers, fetchTasksByDate, postEmployeeOrder } from '../../Utils/api';
import * as XLSX from 'xlsx';
import { formatPhone, validateEmail, validatePhone, normalizePhoneDigits } from '../../Utils/validations';
import DataTable from '../../Components/DataTable/data-table'; // Reusable DataTable component
import PopupMenu from '../../Components/Popup/pop-up';
import EmployeeModal from '../../Components/EmpForm/emp-form';
import EmployeeAnalytics from '../../Components/EmployeeAnalytics/EmployeeAnalytics';
import EmployeeFilters from '../../Components/EmployeeFilters/EmployeeFilters';
import EmployeeBulkActions from '../../Components/EmployeeBulkActions/EmployeeBulkActions';
import SmartInsights from '../../Components/SmartInsights/SmartInsights';
import ActivityTimeline from '../../Components/ActivityTimeline/ActivityTimeline';
import './view-emp.css';
import { Button, TextField, InputAdornment, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Paper, Grid, Typography, Chip, Tooltip, Box, Fade, Grow, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import GroupIcon from '@mui/icons-material/Group';
import TodayIcon from '@mui/icons-material/Today';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useAuth } from '../../Auth/AuthContext';


const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const ViewEmployees = () => {
  const navigate = useNavigate();
  const dataTableRef = useRef(null);
  const [employees, setEmployees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isPopupVisible, setPopupVisible] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [employeeToEdit, setEmployeeToEdit] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [hasDuplicateId, setHasDuplicateId] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const {companyId} = useAuth();

  // Phase 2 & 3: Advanced filters and bulk selection state
  const [designationFilter, setDesignationFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [workloadFilter, setWorkloadFilter] = useState([]);
  const [serviceDayFilter, setServiceDayFilter] = useState([]);
  const [cityFilter, setCityFilter] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [scheduledTodayFilter, setScheduledTodayFilter] = useState([]);

  // Bulk employee import state
  const [bulkEmpUploadOpen, setBulkEmpUploadOpen] = useState(false);
  const [bulkEmpFile, setBulkEmpFile] = useState(null);
  const [bulkEmpUploading, setBulkEmpUploading] = useState(false);
  const [bulkEmpResult, setBulkEmpResult] = useState(null);

  // Phase 1: Employee metrics and workload tracking
  const [workOrders, setWorkOrders] = useState([]);
  const [employeeMetrics, setEmployeeMetrics] = useState({
    total: 0,
    activeToday: 0,
    averageWorkload: 0,
    topPerformer: null
  });
  const [employeeWorkload, setEmployeeWorkload] = useState({});
  const [customers, setCustomers] = useState([]);
  const [employeesScheduledToday, setEmployeesScheduledToday] = useState([]);

  // Handle metric card click - scroll to data table and apply filters
  const handleMetricClick = (filterType) => {
    // Apply appropriate filters based on which metric was clicked
    switch (filterType) {
      case 'total':
        // Clear all filters to show all employees
        setDesignationFilter([]);
        setStatusFilter([]);
        setWorkloadFilter([]);
        setServiceDayFilter([]);
        setCityFilter([]);
        setScheduledTodayFilter([]);
        setSearchQuery('');
        break;
      case 'activeToday':
        // Filter to show employees scheduled today
        setScheduledTodayFilter(employeesScheduledToday);
        setDesignationFilter([]);
        setStatusFilter([]);
        setWorkloadFilter([]);
        setServiceDayFilter([]);
        setCityFilter([]);
        setSearchQuery('');
        break;
      case 'averageWorkload':
        // Clear filters to show all employees with workload visible
        setDesignationFilter([]);
        setStatusFilter([]);
        setWorkloadFilter([]);
        setServiceDayFilter([]);
        setCityFilter([]);
        setScheduledTodayFilter([]);
        setSearchQuery('');
        break;
      case 'topPerformer':
        // Filter to show employees with heavy workload
        setWorkloadFilter(['heavy']);
        setDesignationFilter([]);
        setStatusFilter([]);
        setServiceDayFilter([]);
        setCityFilter([]);
        setScheduledTodayFilter([]);
        setSearchQuery('');
        break;
      default:
        break;
    }

    // Scroll to the data table
    setTimeout(() => {
      if (dataTableRef.current) {
        dataTableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Fetch employees from the backend
  const loadEmployees = useCallback(async () => {
    try {
      const data = await fetchEmployees(companyId, null, null);

      // Check for duplicate employeeIds
      const employeeIdMap = {};
      let duplicateIds = [];
      data.forEach((employee) => {
        if (employeeIdMap[employee.employeeId]) {
          if (!duplicateIds.includes(employee.employeeId)) {
            duplicateIds.push(employee.employeeId);
          }
          employeeIdMap[employee.employeeId]++;
        } else {
          employeeIdMap[employee.employeeId] = 1;
        }
      });

      // Log warning if duplicates found
      if (duplicateIds.length > 0) {
        console.warn(`⚠️ Found ${duplicateIds.length} duplicate employee IDs:`, duplicateIds);
        console.warn('Duplicate employee IDs and their counts:',
          duplicateIds.map(id => `ID ${id}: ${employeeIdMap[id]} employees`).join(', '));
      }

      const formattedData = data.map((employee, index) => ({
        id: employee.employeeId,
        tableRowId: `${employee.employeeId}_${index}`, // Unique ID for table rows
        name: `${employee.firstName} ${employee.lastName}`,
        contact: formatPhone(employee.contactNumber),
        email: employee.email,
        address: `${employee.empAddress}, ${employee.empCity}, ${employee.empZipCode}`,
        designation: employee.empDesignation,
        empObject: employee,
        isDuplicate: employeeIdMap[employee.employeeId] > 1
      }));
      setEmployees(formattedData);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  }, [companyId]);

  // Load routes for workload calculation
  const loadWorkOrders = useCallback(async () => {
    try {
      const data = await fetchAllWorkOrders(companyId);
      setWorkOrders(data);
    } catch (error) {
      console.error('Error loading routes:', error);
    }
  }, [companyId]);

  // Load customers for analytics
  const loadCustomers = useCallback(async () => {
    try {
      const data = await fetchCustomers(companyId, null, null);
      const formattedData = data.map((customer) => ({
        id: customer.customerId,
        name: `${customer.custFirstName} ${customer.custLastName}`,
        serviceDay: customer.serviceDay,
        custObject: customer,
      }));
      setCustomers(formattedData);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  }, [companyId]);

  useEffect(() => { // run on component mount to load emps and routes
    loadEmployees();
    loadWorkOrders();
    loadCustomers();
  }, [loadEmployees, loadWorkOrders, loadCustomers]);

  // Calculate employee metrics and workload
  useEffect(() => {
    if (employees.length === 0) return;

    // Calculate workload per employee (number of routes assigned)
    const workloadMap = {};
    workOrders.forEach(wo => {
      const empId = wo.workOrder?.employeeId;
      if (empId) {
        workloadMap[empId] = (workloadMap[empId] || 0) + 1;
      }
    });
    setEmployeeWorkload(workloadMap);

    // Calculate activeToday by fetching tasks for today
    const calculateActiveToday = async () => {
      try {
        // Get today's date in ISO format
        const today = new Date();
        const isoDate = today.toISOString().split('T')[0];

        // Fetch tasks for today
        const tasksForToday = await fetchTasksByDate(companyId, isoDate);

        // Create a set of active customer IDs for quick lookup
        const activeCustomerIds = new Set(customers.map(c => c.id));

        // Filter tasks and get unique employee IDs
        const employeesWithTasksToday = new Set();
        tasksForToday.forEach(task => {
          // Only count non-cancelled tasks for active customers
          if (task.taskDetails?.taskStatus !== 'Cancelled' &&
              task.customer?.customerId &&
              activeCustomerIds.has(task.customer.customerId) &&
              task.employee?.employeeId) {
            employeesWithTasksToday.add(task.employee.employeeId);
          }
        });

        const activeToday = employeesWithTasksToday.size;

        // Store the list of employee IDs scheduled today for filtering
        setEmployeesScheduledToday(Array.from(employeesWithTasksToday));

        // Calculate other metrics
        const total = employees.length;
        const workloadValues = Object.values(workloadMap);
        const averageWorkload = workloadValues.length > 0
          ? Math.round(workloadValues.reduce((sum, val) => sum + val, 0) / workloadValues.length)
          : 0;

        // Find top performer
        let topPerformer = null;
        let maxWorkload = 0;
        employees.forEach(emp => {
          const workload = workloadMap[emp.id] || 0;
          if (workload > maxWorkload) {
            maxWorkload = workload;
            topPerformer = emp.name;
          }
        });

        setEmployeeMetrics({
          total,
          activeToday,
          averageWorkload,
          topPerformer: topPerformer || 'N/A'
        });
      } catch (error) {
        console.error('Error calculating activeToday metric:', error);
        // Fallback to 0 if there's an error
        setEmployeesScheduledToday([]);

        const total = employees.length;
        const workloadValues = Object.values(workloadMap);
        const averageWorkload = workloadValues.length > 0
          ? Math.round(workloadValues.reduce((sum, val) => sum + val, 0) / workloadValues.length)
          : 0;

        let topPerformer = null;
        let maxWorkload = 0;
        employees.forEach(emp => {
          const workload = workloadMap[emp.id] || 0;
          if (workload > maxWorkload) {
            maxWorkload = workload;
            topPerformer = emp.name;
          }
        });

        setEmployeeMetrics({
          total,
          activeToday: 0,
          averageWorkload,
          topPerformer: topPerformer || 'N/A'
        });
      }
    };

    calculateActiveToday();
  }, [employees, workOrders, companyId, customers]);

  // Helper functions for employee status and workload
  const getWorkloadLevel = (workload) => {
    if (workload === 0) return 'none';
    if (workload < 5) return 'light';
    if (workload < 15) return 'medium';
    return 'heavy';
  };

  const getWorkloadColor = (level) => {
    switch(level) {
      case 'light': return '#10B981'; // Green
      case 'medium': return '#F59E0B'; // Yellow
      case 'heavy': return '#EF4444'; // Red
      default: return '#9CA3AF'; // Gray
    }
  };

  const getWorkloadLabel = (level) => {
    switch(level) {
      case 'light': return 'Light';
      case 'medium': return 'Medium';
      case 'heavy': return 'Heavy';
      default: return 'None';
    }
  };

  const getEmployeeStatus = (employeeId) => {
    const workload = employeeWorkload[employeeId] || 0;
    if (workload > 0) return 'active';
    return 'available';
  };

  const getStatusColor = (status) => {
    return status === 'active' ? '#2196F3' : '#10B981';
  };

  const getStatusLabel = (status) => {
    return status === 'active' ? 'Active' : 'Available';
  };

  // Map each employee to the set of days they service, derived from weekly work orders
  const employeeServiceDaysMap = useMemo(() => {
    const map = {};
    workOrders.forEach(wo => {
      if (!wo.workOrder) return;
      if (wo.workOrder.statusCode?.toLowerCase() === 'cancelled') return;
      if ((wo.workOrder.workOrderOccurance ?? 0) !== 7) return;
      const empId = wo.workOrder.employeeId;
      if (!empId) return;
      const dateStr = (wo.workOrder.workOrderDueDate?.trim() || wo.workOrder.initialServiceDate?.trim() || '');
      if (!dateStr) return;
      const woDate = new Date(dateStr.split('T')[0] + 'T12:00:00');
      const dayName = DAYS_OF_WEEK[woDate.getDay()];
      if (!map[empId]) map[empId] = new Set();
      map[empId].add(dayName);
    });
    const result = {};
    Object.entries(map).forEach(([id, days]) => {
      result[id] = Array.from(days);
    });
    return result;
  }, [workOrders]);

  // Filter employees based on search query and all filters
  const filteredEmployees = employees.filter((employee) => {
    // Search query filter
    if (!employee.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }

    // Designation filter
    if (designationFilter.length > 0 && !designationFilter.includes(employee.empObject?.empDesignation)) {
      return false;
    }

    // Status filter
    if (statusFilter.length > 0) {
      const status = getEmployeeStatus(employee.id);
      if (!statusFilter.includes(status)) {
        return false;
      }
    }

    // Workload filter
    if (workloadFilter.length > 0) {
      const workload = employeeWorkload[employee.id] || 0;
      const level = getWorkloadLevel(workload);
      if (!workloadFilter.includes(level)) {
        return false;
      }
    }

    // Service day filter
    if (serviceDayFilter.length > 0) {
      const days = employeeServiceDaysMap[employee.id] || [];
      if (!days.some(d => serviceDayFilter.includes(d))) {
        return false;
      }
    }

    // City filter
    if (cityFilter.length > 0) {
      const city = employee.empObject?.empCity;
      if (!city || !cityFilter.includes(city)) {
        return false;
      }
    }

    // Scheduled today filter
    if (scheduledTodayFilter.length > 0) {
      if (!scheduledTodayFilter.includes(employee.id)) {
        return false;
      }
    }

    return true;
  });

  // Columns for DataTable
  const columns = [
    {
      field: 'name',
      headerName: 'Employee Name',
      width: 160,
      minWidth: 140,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {params.row.isDuplicate && (
            <WarningIcon
              sx={{ color: '#ff9800', fontSize: '1.2rem' }}
              titleAccess="Duplicate Employee ID detected"
            />
          )}
          <span>{params.value}</span>
        </div>
      ),
    },
    { field: 'contact', headerName: 'Phone', width: 140, minWidth: 130 },
    { field: 'email', headerName: 'Email', width: 210, minWidth: 180 },
    { field: 'designation', headerName: 'Designation', width: 120, minWidth: 100 },
    {
      field: 'status',
      headerName: 'Status',
      headerAlign: 'center',
      align: 'center',
      width: 120,
      renderCell: (params) => {
        const status = getEmployeeStatus(params.row.id);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Chip
              label={getStatusLabel(status)}
              size="small"
              sx={{
                backgroundColor: getStatusColor(status),
                color: 'white',
                fontWeight: 600,
                fontSize: '0.75rem'
              }}
            />
          </Box>
        );
      }
    },
    {
      field: 'workload',
      headerName: 'Workload',
      headerAlign: 'center',
      align: 'center',
      width: 150,
      renderCell: (params) => {
        const workload = employeeWorkload[params.row.id] || 0;
        const level = getWorkloadLevel(workload);
        const tooltipText = {
          'none': 'No routes assigned',
          'light': '1-4 routes (light workload)',
          'medium': '5-14 routes (medium workload)',
          'heavy': '15+ routes (heavy workload)'
        };
        return (
          <Tooltip title={tooltipText[level]} arrow placement="top">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', height: '100%' }}>
              <Chip
                label={getWorkloadLabel(level)}
                size="small"
                sx={{
                  backgroundColor: getWorkloadColor(level),
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.75rem'
                }}
              />
              <Typography variant="caption" sx={{ color: '#666' }}>
                ({workload})
              </Typography>
            </Box>
          </Tooltip>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      headerAlign: 'center',
      align: 'center',
      minWidth: 320,
      width: 320,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', gap: '6px' }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleEditClick(params.row.empObject)}
            sx={{ fontSize: '0.7rem', padding: '4px 10px', whiteSpace: 'nowrap' }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleViewMoreClick(params.row.empObject)}
            sx={{ fontSize: '0.7rem', padding: '4px 10px', whiteSpace: 'nowrap' }}
          >
            View
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleViewRoutesClick(params.row.empObject)}
            sx={{ fontSize: '0.7rem', padding: '4px 10px', whiteSpace: 'nowrap' }}
          >
            Routes
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => handleDeleteClick(params.row.empObject)}
            sx={{ fontSize: '0.7rem', padding: '4px 10px', whiteSpace: 'nowrap' }}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  const handleEditClick = (employee) => {
    setEmployeeToEdit(employee);
    setEditModalOpen(true);
  };

  const handleAddEmployee = () => {
    navigate('/add-emp');
  };

  // Bulk employee import handlers
  const handleDownloadEmpTemplate = () => {
    const headers = ['First Name', 'Last Name', 'Phone Number', 'Email Address', 'Designation', 'Address', 'City', 'State', 'Zip Code'];
    const sampleData = [
      ['John', 'Smith', '2105551234', 'john.smith@company.com', 'Technician', '123 Main St', 'Phoenix', 'AZ', '85001'],
      ['Jane', 'Doe', '2129876543', 'jane.doe@company.com', 'Manager', '456 Oak Ave', 'Scottsdale', 'AZ', '85250'],
    ];
    const instructions = [
      ['Field', 'Required', 'Notes'],
      ['First Name', 'Yes', 'Text'],
      ['Last Name', 'Yes', 'Text'],
      ['Phone Number', 'Yes', '10-digit US number (e.g. 2105551234 or 210-555-1234)'],
      ['Email Address', 'Yes', 'Valid email — employee receives login invitation'],
      ['Designation', 'Yes', 'Owner | Manager | Technician | Partner'],
      ['Address', 'Yes', 'Street address'],
      ['City', 'Yes', 'Text'],
      ['State', 'Yes', '2-letter state abbreviation (e.g. AZ)'],
      ['Zip Code', 'Yes', '5-digit zip code'],
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, ...sampleData]);
    ws['!cols'] = [{ wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 28 }, { wch: 14 }, { wch: 25 }, { wch: 14 }, { wch: 8 }, { wch: 10 }];
    const instrSheet = XLSX.utils.aoa_to_sheet(instructions);
    instrSheet['!cols'] = [{ wch: 16 }, { wch: 10 }, { wch: 60 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Employees');
    XLSX.utils.book_append_sheet(wb, instrSheet, 'Instructions');
    XLSX.writeFile(wb, 'employee_import_template.xlsx');
  };

  const handleEmpFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!['.xlsx', '.xls'].includes(ext)) {
      alert('Please upload an Excel file (.xlsx or .xls). Apple Numbers files must be exported as Excel first (File → Export To → Excel).');
      e.target.value = '';
      return;
    }
    setBulkEmpFile(file);
  };

  const handleBulkEmpUpload = async () => {
    if (!bulkEmpFile) return;
    setBulkEmpUploading(true);
    setBulkEmpResult(null);

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

        if (rows.length < 2) {
          setBulkEmpResult({ errors: ['File is empty or has no data rows.'], added: 0, failed: [] });
          setBulkEmpUploading(false);
          return;
        }

        const headersLower = rows[0].map(h => String(h).trim().toLowerCase());
        const getIdx = (...names) => { for (const n of names) { const i = headersLower.indexOf(n.toLowerCase()); if (i !== -1) return i; } return -1; };

        const firstIdx = getIdx('first name', 'firstname');
        const lastIdx = getIdx('last name', 'lastname');
        const phoneIdx = getIdx('phone number', 'phone', 'contact no', 'custcontactno');
        const emailIdx = getIdx('email address', 'email');
        const desigIdx = getIdx('designation', 'position', 'role');
        const addrIdx = getIdx('address');
        const cityIdx = getIdx('city');
        const stateIdx = getIdx('state');
        const zipIdx = getIdx('zip code', 'zip', 'zipcode');

        const VALID_DESIGNATIONS = ['owner', 'manager', 'technician', 'partner'];
        const errors = [];
        const toAdd = [];

        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];
          if (!row || row.every(c => c === '' || c === null || c === undefined)) continue;
          const rowNum = i + 1;
          const fn = firstIdx !== -1 ? String(row[firstIdx] ?? '').trim() : '';
          const ln = lastIdx !== -1 ? String(row[lastIdx] ?? '').trim() : '';
          const phone = phoneIdx !== -1 ? String(row[phoneIdx] ?? '').trim() : '';
          const email = emailIdx !== -1 ? String(row[emailIdx] ?? '').trim().toLowerCase() : '';
          const desig = desigIdx !== -1 ? String(row[desigIdx] ?? '').trim() : '';
          const addr = addrIdx !== -1 ? String(row[addrIdx] ?? '').trim() : '';
          const city = cityIdx !== -1 ? String(row[cityIdx] ?? '').trim() : '';
          const state = stateIdx !== -1 ? String(row[stateIdx] ?? '').trim() : '';
          const zip = zipIdx !== -1 ? String(row[zipIdx] ?? '').trim() : '';

          const rowErrors = [];
          if (!fn) rowErrors.push('First Name required');
          if (!ln) rowErrors.push('Last Name required');
          if (!email || !validateEmail(email).isValid) rowErrors.push('Valid Email Address required');
          const normPhone = normalizePhoneDigits(phone);
          if (!normPhone || normPhone.length !== 10) rowErrors.push('Valid 10-digit Phone Number required');
          if (!desig || !VALID_DESIGNATIONS.includes(desig.toLowerCase())) rowErrors.push(`Designation must be one of: Owner, Manager, Technician, Partner (got "${desig}")`);
          if (!addr) rowErrors.push('Address required');
          if (!city) rowErrors.push('City required');
          if (!state) rowErrors.push('State required');
          if (!zip || !/^\d{5}$/.test(zip)) rowErrors.push('Valid 5-digit Zip Code required');

          if (rowErrors.length > 0) {
            errors.push(`Row ${rowNum} (${email || fn}): ${rowErrors.join('; ')}`);
          } else {
            toAdd.push({ fn, ln, normPhone, email, desig: desig.charAt(0).toUpperCase() + desig.slice(1).toLowerCase(), addr, city, state, zip });
          }
        }

        if (errors.length > 0) {
          setBulkEmpResult({ errors, added: 0, failed: [] });
          setBulkEmpUploading(false);
          return;
        }

        // Submit each employee
        const added = [];
        const failed = [];
        for (const emp of toAdd) {
          try {
            await postEmployeeOrder({
              username: emp.email,
              email: emp.email,
              firstName: emp.fn,
              lastName: emp.ln,
              contactNumber: '+1' + emp.normPhone,
              empDesignation: emp.desig,
              empAddress: emp.addr,
              empCity: emp.city,
              empState: emp.state,
              empZipCode: emp.zip,
              companyId: companyId,
              currRecInd: 'Y',
            });
            added.push(`${emp.fn} ${emp.ln}`);
          } catch (err) {
            const msg = err.response?.data?.message || err.message || 'Unknown error';
            failed.push(`${emp.fn} ${emp.ln}: ${msg}`);
          }
        }

        setBulkEmpResult({ errors: [], added: added.length, failed });
        setBulkEmpFile(null);
        await loadEmployees();
      } catch (err) {
        setBulkEmpResult({ errors: [`Failed to parse file: ${err.message}`], added: 0, failed: [] });
      }
      setBulkEmpUploading(false);
    };
    reader.readAsArrayBuffer(bulkEmpFile);
  };

  const handleViewMoreClick = (employee) => {
    setSelectedEmployee(employee);
    setPopupVisible(true);
  };

     const handleViewRoutesClick = (employee) => {
  // Navigate to Employee Scheduling page with employee data
  navigate('/employee-scheduling', {
    state: {
      employeeId: employee.employeeId,
      employeeName: `${employee.firstName} ${employee.lastName}`
    }
  });
};
  

  const onEmployeeEdit = async (updatedData) => {
    setEditModalOpen(false);
    try {
      const response = await editEmployee(updatedData);
      if(response.status === 200) {
        await loadEmployees();
      }
      else {
        console.error("Error updating employee:", response);
      }
    }
    catch (error) {
      console.error("Error editing Employee info: ", error);
    }
  }

  const handleDeleteClick = (employee) => {
    // Check if this employee has a duplicate ID
    const duplicates = employees.filter(emp => emp.empObject.employeeId === employee.employeeId);
    setHasDuplicateId(duplicates.length > 1);
    setDuplicateCount(duplicates.length);
    setEmployeeToDelete(employee);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!employeeToDelete) return;

    try {
      const response = await deleteEmployee(companyId, employeeToDelete.employeeId);
      if (response.status === 200 || response.status === 204) {
        setDeleteDialogOpen(false);
        setEmployeeToDelete(null);
        await loadEmployees();
        alert('Employee deleted successfully');
      } else {
        console.error("Error deleting employee:", response);
        const errorMessage = response.data?.message || response.data?.error || 'Failed to delete employee. Please try again.';
        alert(`Failed to delete employee: ${errorMessage}`);
      }
    } catch (error) {
      console.error("Error deleting Employee: ", error);
      // Extract more detailed error information
      let errorMessage = 'Failed to delete employee. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.statusText) {
        errorMessage = `Error: ${error.response.statusText}`;
      } else if (error.message) {
        errorMessage = error.message;
      }
      alert(`Failed to delete employee: ${errorMessage}`);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setEmployeeToDelete(null);
  };

  return (
    <div className="employee-list">
      {/* Employee Metrics Dashboard */}
      <Fade in={true} timeout={800}>
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: { xs: 2, sm: 3 },
            background: 'linear-gradient(135deg, #003366 0%, #004080 100%)',
            borderRadius: 2
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: 'white',
              mb: 2,
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <TrendingUpIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /> Employee Overview
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {/* Total Employees */}
            <Grid item xs={6} sm={6} md={3}>
              <Grow in={true} timeout={600}>
                <Paper
                  elevation={0}
                  onClick={() => handleMetricClick('total')}
                  sx={{
                    p: { xs: 1.5, sm: 2.5 },
                    textAlign: 'center',
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  <GroupIcon sx={{ fontSize: { xs: 30, sm: 40 }, color: '#003366', mb: { xs: 0.5, sm: 1 } }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#003366', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    {employeeMetrics.total}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                    Total Employees
                  </Typography>
                </Paper>
              </Grow>
            </Grid>

            {/* Active Today */}
            <Grid item xs={6} sm={6} md={3}>
              <Grow in={true} timeout={800}>
                <Paper
                  elevation={0}
                  onClick={() => handleMetricClick('activeToday')}
                  sx={{
                    p: { xs: 1.5, sm: 2.5 },
                    textAlign: 'center',
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  <TodayIcon sx={{ fontSize: { xs: 30, sm: 40 }, color: '#2196F3', mb: { xs: 0.5, sm: 1 } }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#2196F3', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    {employeeMetrics.activeToday}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                    No. of Employees Scheduled Today
                  </Typography>
                </Paper>
              </Grow>
            </Grid>

            {/* Average Workload */}
            <Grid item xs={6} sm={6} md={3}>
              <Grow in={true} timeout={1000}>
                <Tooltip
                  title="Average number of routes per employee. Click to view all employees."
                  arrow
                  placement="top"
                >
                  <Paper
                    elevation={0}
                    onClick={() => handleMetricClick('averageWorkload')}
                    sx={{
                      p: { xs: 1.5, sm: 2.5 },
                      textAlign: 'center',
                      background: 'rgba(255, 255, 255, 0.95)',
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.15)'
                      }
                    }}
                  >
                    <AssessmentIcon sx={{ fontSize: { xs: 30, sm: 40 }, color: '#10B981', mb: { xs: 0.5, sm: 1 } }} />
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#10B981', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      {employeeMetrics.averageWorkload}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                      Average Workload
                    </Typography>
                  </Paper>
                </Tooltip>
              </Grow>
            </Grid>

            {/* Top Performer */}
            <Grid item xs={6} sm={6} md={3}>
              <Grow in={true} timeout={1200}>
                <Tooltip
                  title="Employee with the most routes assigned. Click to view employees with heavy workload."
                  arrow
                  placement="top"
                >
                  <Paper
                    elevation={0}
                    onClick={() => handleMetricClick('topPerformer')}
                    sx={{
                      p: { xs: 1.5, sm: 2.5 },
                      textAlign: 'center',
                      background: 'rgba(255, 255, 255, 0.95)',
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.15)'
                      }
                    }}
                  >
                    <EmojiEventsIcon sx={{ fontSize: { xs: 30, sm: 40 }, color: '#F59E0B', mb: { xs: 0.5, sm: 1 } }} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#F59E0B',
                        fontSize: { xs: '0.9rem', sm: '1.25rem' },
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {employeeMetrics.topPerformer}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                      Top Performer
                    </Typography>
                  </Paper>
                </Tooltip>
              </Grow>
            </Grid>
          </Grid>
        </Paper>
      </Fade>

      {/* Phase 2: Employee Analytics Charts */}
      <EmployeeAnalytics
        employees={employees}
        workOrders={workOrders}
        employeeWorkload={employeeWorkload}
        customers={customers}
      />

      {/* Phase 3: Smart Insights */}
      <SmartInsights
        employees={employees}
        employeeWorkload={employeeWorkload}
      />

      {/* Phase 2: Advanced Filters */}
      <EmployeeFilters
        employees={employees}
        employeeWorkload={employeeWorkload}
        employeeServiceDaysMap={employeeServiceDaysMap}
        designationFilter={designationFilter}
        statusFilter={statusFilter}
        workloadFilter={workloadFilter}
        serviceDayFilter={serviceDayFilter}
        cityFilter={cityFilter}
        onDesignationChange={setDesignationFilter}
        onStatusChange={setStatusFilter}
        onWorkloadChange={setWorkloadFilter}
        onServiceDayChange={setServiceDayFilter}
        onCityChange={setCityFilter}
      />

      {/* Phase 2: Bulk Actions Toolbar */}
      <EmployeeBulkActions
        selectedEmployees={selectedEmployees}
        onClearSelection={() => setSelectedEmployees([])}
      />

      {/* DataTable Component */}
      <Box ref={dataTableRef}>
        <DataTable
          rows={filteredEmployees}
          columns={columns}
          getRowId={(row) => row.tableRowId}
          title="Manage Employees"
          checkboxSelection
          onSelectionModelChange={(newSelection) => {
            const selected = filteredEmployees
              .filter(emp => newSelection.includes(emp.tableRowId))
              .map(emp => emp.empObject);
            setSelectedEmployees(selected);
          }}
          headerActions={
          <>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddEmployee}
              sx={{ backgroundColor: '#003366', '&:hover': { backgroundColor: '#002244' }, textTransform: 'none', fontWeight: 'bold' }}
            >
              Add Employee
            </Button>
            <Tooltip title="Upload an Excel file to add employees in bulk" arrow>
              <Button
                variant="contained"
                startIcon={<UploadIcon />}
                onClick={() => setBulkEmpUploadOpen(true)}
                sx={{ backgroundColor: '#006644', '&:hover': { backgroundColor: '#004422' }, textTransform: 'none', fontWeight: 'bold' }}
              >
                Bulk Import Employees
              </Button>
            </Tooltip>
          </>
        }
        searchComponent={
          <TextField
            placeholder="Search by Employee Name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              backgroundColor: '#fff',
              borderRadius: '5px',
              width: '300px'
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        }
      />
      </Box>

      {/* Pop-up Modal */}
      {selectedEmployee && (
        <PopupMenu
          isVisible={isPopupVisible}
          PersonData={selectedEmployee}
          onClose={() => setPopupVisible(false)}
        />
      )}
      {/* Edit Employee Modal */}
      {isEditModalOpen && (
        <EmployeeModal
          open={isEditModalOpen}
          onClose={() => setEditModalOpen(false)}
          onEmployeeAdded={loadEmployees}
          initialData={employeeToEdit}
          onEmployeeEdit={onEmployeeEdit}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title" sx={{ color: '#002244', fontWeight: 600 }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete employee{' '}
            <strong>
              {employeeToDelete
                ? `${employeeToDelete.firstName} ${employeeToDelete.lastName}`
                : ''}
            </strong>
            {employeeToDelete && ` (ID: ${employeeToDelete.employeeId})`}
            ? This action cannot be undone.
          </DialogContentText>
          {hasDuplicateId && (
            <DialogContentText
              sx={{
                marginTop: 2,
                padding: 2,
                backgroundColor: '#fff3cd',
                color: '#856404',
                borderRadius: 1,
                border: '1px solid #ffc107'
              }}
            >
              <strong>⚠️ WARNING: Duplicate Employee ID Detected!</strong>
              <br />
              There are {duplicateCount} employees with the same ID ({employeeToDelete?.employeeId}).
              This is a data integrity issue that may prevent deletion.
              <br /><br />
              <strong>Possible issues:</strong>
              <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '20px' }}>
                <li>The backend may try to delete multiple employees at once</li>
                <li>Database constraints may block the deletion</li>
                <li>If this employee is assigned to routes, deletion will fail</li>
              </ul>
              <strong>Recommendation:</strong> Contact your system administrator to resolve the duplicate employee ID issue in the database.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary" variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            autoFocus
          >
            {hasDuplicateId ? 'Try Delete Anyway' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Phase 3: Activity Timeline */}
      <ActivityTimeline
        employees={employees}
        employeeWorkload={employeeWorkload}
        workOrders={workOrders}
      />

      {/* Bulk Employee Import Dialog */}
      <Dialog open={bulkEmpUploadOpen} onClose={bulkEmpUploading ? undefined : () => { setBulkEmpUploadOpen(false); setBulkEmpFile(null); setBulkEmpResult(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Import Employees</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload an Excel file (.xlsx) to add multiple employees at once. Each employee will receive a login invitation email.
            </Typography>

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Step 1 — Download Template</Typography>
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5, mb: 2 }}>
              <Button variant="outlined" size="small" onClick={handleDownloadEmpTemplate} sx={{ mb: 0.5 }}>
                Employee Import Template
              </Button>
              <Typography variant="caption" display="block" color="text.secondary">
                Required: First Name, Last Name, Phone Number, Email Address, Designation (Owner/Manager/Technician/Partner), Address, City, State, Zip Code.
              </Typography>
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Step 2 — Fill & Upload</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Phone number can be in any US format: <strong>2105551234</strong>, <strong>210-555-1234</strong>, or <strong>+1 (210) 555-1234</strong>.
            </Typography>
          </Box>

          <Box sx={{ mt: 1, mb: 2 }}>
            <input
              type="file"
              id="bulk-emp-file-input"
              accept=".xlsx, .xls"
              onChange={handleEmpFileUpload}
              style={{ display: 'none' }}
              disabled={bulkEmpUploading}
            />
            <label htmlFor="bulk-emp-file-input">
              <Button variant="contained" component="span" disabled={bulkEmpUploading} sx={{ textTransform: 'none' }}>
                Choose File
              </Button>
            </label>
          </Box>

          {bulkEmpFile && !bulkEmpResult && (
            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold' }}>
              Selected: {bulkEmpFile.name}
            </Typography>
          )}
          {bulkEmpUploading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="primary.main">Processing employees... do not close.</Typography>
            </Box>
          )}
          {bulkEmpResult && (
            <Box sx={{ mt: 2 }}>
              {bulkEmpResult.errors.length > 0 && (
                <Box>
                  <Typography variant="body2" color="error" fontWeight="bold">Validation errors — fix and re-upload:</Typography>
                  <ul style={{ margin: '8px 0', paddingLeft: 20, maxHeight: 200, overflowY: 'auto' }}>
                    {bulkEmpResult.errors.map((e, i) => <li key={i} style={{ fontSize: '0.8rem', color: '#ef4444', marginBottom: 4 }}>{e}</li>)}
                  </ul>
                </Box>
              )}
              {bulkEmpResult.added > 0 && (
                <Typography variant="body2" color="success.main" fontWeight="bold">
                  Successfully added {bulkEmpResult.added} employee(s).
                </Typography>
              )}
              {bulkEmpResult.failed.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="error" fontWeight="bold">{bulkEmpResult.failed.length} employee(s) failed:</Typography>
                  <ul style={{ margin: '8px 0', paddingLeft: 20, maxHeight: 150, overflowY: 'auto' }}>
                    {bulkEmpResult.failed.map((f, i) => <li key={i} style={{ fontSize: '0.8rem', color: '#ef4444', marginBottom: 4 }}>{f}</li>)}
                  </ul>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBulkEmpUploadOpen(false); setBulkEmpFile(null); setBulkEmpResult(null); }} disabled={bulkEmpUploading}>
            {bulkEmpResult ? 'Close' : 'Cancel'}
          </Button>
          {!bulkEmpResult && (
            <Button onClick={handleBulkEmpUpload} variant="contained" disabled={!bulkEmpFile || bulkEmpUploading}>
              {bulkEmpUploading ? 'Uploading...' : 'Upload'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ViewEmployees;